import { useState } from "react";
import { AlertTriangle, ArrowRight, Check, Copy, Lock } from "lucide-react";

import AIAssistField from "../AIAssistField";
import { ExportButtons, type ExportFormat } from "../ExportButtons";
import { Field } from "../FormLayout";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import type { GovernanceAccountBalance, GovernanceApproval, GovernanceBudget, GovernanceDashboard, GovernanceGrant, GovernanceStaffPay } from "../../services/financialService";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../../utils/exportTable";
import {
  ACCOUNT_TYPES, accountConfig, approvalConfig, budgetConfig, budgetStatus, documentLabel, grantConfig, grantStatus, money, num,
  overviewRows, payConfig, percent, restrictionLabel, shortDate, stamp,
} from "./governanceLogic";

type Go = (tab: string) => void;

/** Spent against planned, as a bar; red once past the whole. */
export function UsageBar({ value, label }: { value: string | number; label?: string }) {
  const used = num(value);
  return (
    <span className="flex min-w-[7rem] items-center gap-2" title={label}>
      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
        <span className={`block h-full rounded-full ${used > 100 ? "bg-red-500" : "bg-primary-500"}`} style={{ width: `${Math.min(used, 100)}%` }} />
      </span>
      <span className={`w-12 text-right text-xs tabular-nums ${used > 100 ? "font-semibold text-red-700" : "text-gray-600"}`}>{percent(used)}</span>
    </span>
  );
}

// ---- Overview ---------------------------------------------------------------------------------

/** A control check, or "Not measured" with the reason while the chart of accounts is unfinished. */
const controlCheck = (label: string, note: string, difference: string | null, missing: string) =>
  difference === null
    ? { label, value: "Not measured", extra: "", note: missing || "The chart of accounts is not finished.", alert: true }
    : { label, value: money(difference), extra: "", note, alert: num(difference) !== 0 };

export function OverviewPanel({ data, go, canWrite }: { data: GovernanceDashboard; go: Go; canWrite: boolean }) {
  const position = data.financial_position;
  const exceptions = data.exceptions;
  const period = `${shortDate(data.period.start)} – ${shortDate(data.period.end)}`;
  const rows = overviewRows(data);
  const exportRows = (format: ExportFormat) => {
    const columns = [
      { header: "Section", accessor: (row: (typeof rows)[number]) => row.section },
      { header: "Figure", accessor: (row: (typeof rows)[number]) => row.figure },
      { header: "Value", accessor: (row: (typeof rows)[number]) => row.value },
    ];
    const title = `Governance overview, ${period}`;
    if (format === "csv") exportAsCsv(`governance-overview-${data.period.end}.csv`, columns, rows);
    if (format === "excel") exportAsExcel(`governance-overview-${data.period.end}.xls`, title, columns, rows);
    if (format === "pdf") exportAsPdf(title, columns, rows);
  };
  const surplus = position ? num(position.surplus_or_deficit) : 0;
  const tiles = position
    ? [
        { label: "Assets", value: money(position.assets), note: `As at ${shortDate(position.as_of)}` },
        { label: "Liabilities", value: money(position.liabilities), note: "What the school owes" },
        { label: "Net assets", value: money(position.net_assets), note: "Assets less liabilities" },
        { label: "Income", value: money(position.income), note: `Earned ${period}` },
        { label: "Expenses", value: money(position.expenses), note: `Spent ${period}` },
        { label: surplus < 0 ? "Deficit" : "Surplus", value: money(Math.abs(surplus)), note: "Income less expenses", alert: surplus < 0 },
      ]
    : [];
  const checks = exceptions
    ? [
        { label: "Overdue fee invoices", value: `${exceptions.overdue_invoices}`, extra: exceptions.overdue_invoices ? `owing ${money(exceptions.overdue_amount)}` : "", note: "Fees past their due date and not yet paid.", alert: exceptions.overdue_invoices > 0 },
        { label: "Bank lines not matched", value: `${exceptions.unreconciled_bank_lines}`, note: "Money in or out of the bank not yet tied to the books.", alert: exceptions.unreconciled_bank_lines > 0 },
        { label: "Provider rows not resolved", value: `${exceptions.unresolved_provider_rows}`, note: "Mobile-money or bank payments that match no learner or invoice.", alert: exceptions.unresolved_provider_rows > 0 },
        controlCheck("Receivables control difference", "Should be nil: what learners owe in the ledger against their invoices.", exceptions.receivables_control_difference, exceptions.receivables_control_note),
        controlCheck("Payables control difference", "Should be nil: what the ledger owes suppliers against their bills.", exceptions.payables_control_difference, exceptions.payables_control_note),
      ]
    : [];
  const received = data.grants.reduce((sum, grant) => sum + num(grant.received), 0);
  const spent = data.grants.reduce((sum, grant) => sum + num(grant.spent), 0);
  const topBudgets = [...(data.budgets ?? [])].sort((a, b) => num(b.utilization_percent) - num(a.utilization_percent)).slice(0, 4);

  if (!position && !exceptions && !data.budgets && !data.approvals && !data.grants.length) {
    return (
      <div className="card card-content py-10 text-center text-sm text-gray-500">
        <Lock className="mx-auto mb-2 h-6 w-6 text-gray-400" />
        Your access does not show any figures yet. Ask the school to choose what you may see.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">Balances as at <span className="font-medium text-gray-700">{shortDate(data.period.end)}</span>; income, spending and approvals for <span className="font-medium text-gray-700">{period}</span>.</p>
        <ExportButtons onExport={exportRows} disabled={!rows.length} what="these figures" />
      </div>

      {position && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map((tile) => (
            <div key={tile.label} className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium text-gray-500">{tile.label}</p>
              <p className={`mt-1 text-xl font-semibold tabular-nums ${tile.alert ? "text-red-700" : "text-gray-900"}`}>{tile.value}</p>
              <p className="mt-0.5 text-xs text-gray-500">{tile.note}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {exceptions && (
          <section className="card">
            <div className="card-titlebar"><h3 className="font-semibold text-gray-900">Needs management attention</h3><span className="text-xs text-gray-500">Today</span></div>
            <ul className="divide-y divide-gray-100">
              {checks.map((check) => (
                <li key={check.label} className="flex items-start justify-between gap-4 px-6 py-3">
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">{check.alert && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />}{check.label}</span>
                    <span className="block text-xs text-gray-500">{check.note}</span>
                  </span>
                  <span className={`shrink-0 text-right text-sm font-semibold tabular-nums ${check.alert ? "text-amber-800" : "text-gray-500"}`}>
                    {check.value}
                    {check.extra && <span className="block text-xs font-normal text-gray-500">{check.extra}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="space-y-6">
          {data.budgets && (
            <section className="card">
              <div className="card-titlebar"><h3 className="font-semibold text-gray-900">Budgets, most used first</h3><button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline" onClick={() => go("budgets")}>All budgets <ArrowRight className="h-3.5 w-3.5" /></button></div>
              {topBudgets.length ? (
                <ul className="divide-y divide-gray-100">
                  {topBudgets.map((budget) => (
                    <li key={budget.id} className="grid grid-cols-[1fr_auto] items-center gap-x-4 px-6 py-3 text-sm">
                      <span className="min-w-0"><span className="block truncate font-medium text-gray-900">{budget.name}</span><span className="text-xs text-gray-500">{money(budget.actual_total)} of {money(budget.budget_total)}</span></span>
                      <UsageBar value={budget.utilization_percent} />
                    </li>
                  ))}
                </ul>
              ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No approved budgets yet.</p>}
            </section>
          )}
          {data.scope.grant_scope !== "none" && (
            <section className="card">
              <div className="card-titlebar"><h3 className="font-semibold text-gray-900">Grants and donor funds</h3><button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline" onClick={() => go("grants")}>All grants <ArrowRight className="h-3.5 w-3.5" /></button></div>
              {data.grants.length ? (
                <div className="grid gap-3 px-6 py-4 text-sm sm:grid-cols-3">
                  <div><p className="text-xs text-gray-500">Received</p><p className="font-semibold tabular-nums text-gray-900">{money(received)}</p></div>
                  <div><p className="text-xs text-gray-500">Spent</p><p className="font-semibold tabular-nums text-gray-900">{money(spent)}</p></div>
                  <div><p className="text-xs text-gray-500">Left to spend</p><p className="font-semibold tabular-nums text-gray-900">{money(received - spent)}</p></div>
                  <p className="text-xs text-gray-500 sm:col-span-3">{data.grants.length} {data.grants.length === 1 ? "grant" : "grants"} in your view.</p>
                </div>
              ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No approved grants are in your view.</p>}
            </section>
          )}
        </div>
      </div>

      {canWrite && <BoardSummary period={data.period} />}
    </div>
  );
}

/** A plain summary of the figures above for a board paper; nothing here is saved. */
function BoardSummary({ period }: { period: { start: string; end: string } }) {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <section className="card">
      <div className="card-titlebar">
        <div>
          <h3 className="font-semibold text-gray-900">Summary for a board paper</h3>
          <p className="text-xs text-gray-500">Write it yourself, or have it drafted from the figures you can see for {shortDate(period.start)} – {shortDate(period.end)}. Nothing here is saved; copy it into your paper.</p>
        </div>
      </div>
      <div className="card-content space-y-3 pt-5">
        <Field label="Summary" as="div">
          <AIAssistField
            rows={6}
            value={text}
            onChange={(next) => setText(next)}
            placeholder="e.g. Fees collection is behind: 42 invoices overdue. The library grant is 38% spent."
            context={{ key: "financial.governance_note", params: { kind: "brief", date_from: period.start, date_to: period.end } }}
            draftLabel="Draft from these figures"
            draftHint="It uses only the figures your access shows, exactly as shown. It gives no reasons or forecasts: add those yourself."
          />
        </Field>
        <div className="flex justify-end">
          <button type="button" className="btn btn-outline gap-1.5" disabled={!text.trim()} onClick={copy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
    </section>
  );
}

// ---- Budgets ----------------------------------------------------------------------------------

export function BudgetsPanel({ rows, today, drillDown }: { rows: GovernanceBudget[]; today: string; drillDown: boolean }) {
  const [viewing, setViewing] = useState<number | null>(null);
  const columns: RegisterColumn<GovernanceBudget>[] = [
    { header: "Budget", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Period", cell: (row) => <span className="whitespace-nowrap">{shortDate(row.start_date)} – {shortDate(row.end_date)}</span>, exportValue: (row) => `${row.start_date} to ${row.end_date}` },
    { header: "Budget", cell: (row) => money(row.budget_total), exportValue: (row) => num(row.budget_total), align: "right" },
    { header: "Actual", cell: (row) => money(row.actual_total), exportValue: (row) => num(row.actual_total), align: "right" },
    { header: "Left", cell: (row) => <span className={num(row.variance) < 0 ? "text-red-700" : undefined}>{money(row.variance)}</span>, exportValue: (row) => num(row.variance), align: "right" },
    { header: "Used", cell: (row) => <UsageBar value={row.utilization_percent} />, exportValue: (row) => `${row.utilization_percent}%` },
    { header: "Status", cell: (row) => <StatusDot {...budgetStatus(row, today)} />, exportValue: (row) => budgetStatus(row, today).label },
    { header: "Approved by", cell: (row) => row.approved_by, exportValue: (row) => row.approved_by, exportOnly: true },
  ];
  const row = viewing ? rows.find((item) => item.id === viewing) : undefined;
  return (
    <>
      <ClientRegister
        title="Approved budgets"
        description="Each approved budget against what was actually posted to its accounts over the budget's own dates."
        rows={rows}
        config={budgetConfig(today)}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["budget", "budgets"]}
        exportName={{ file: "governance-budgets", title: "Approved Budgets" }}
        emptyText="No approved budgets yet."
      />
      {row && (
        <DetailsModal
          size="3xl"
          title={row.name}
          headline={{ label: "Actual against budget", value: `${money(row.actual_total)} of ${money(row.budget_total)}`, note: `${percent(row.utilization_percent)} used · ${shortDate(row.start_date)} – ${shortDate(row.end_date)}` }}
          status={budgetStatus(row, today)}
          facts={[["Left to spend", money(row.variance)], ["Budget lines", String(row.line_count)], ["Approved by", row.approved_by || "Not recorded"], ["Approved on", stamp(row.approved_at) || "—"]]}
          onClose={() => setViewing(null)}
        >
          {row.lines ? (
            <div className="sheet-wrap">
              <table className="sheet">
                <thead><tr><th>Account</th><th className="text-right">Budget</th><th className="text-right">Actual</th><th className="text-right">Left</th><th>Used</th></tr></thead>
                <tbody>
                  {row.lines.map((line) => (
                    <tr key={line.account_code}>
                      <td><span className="tabular-nums text-gray-500">{line.account_code}</span> {line.account_name}</td>
                      <td className="text-right tabular-nums">{money(line.budget)}</td>
                      <td className="text-right tabular-nums">{money(line.actual)}</td>
                      <td className={`text-right tabular-nums ${num(line.variance) < 0 ? "text-red-700" : ""}`}>{money(line.variance)}</td>
                      <td><UsageBar value={line.utilization_percent} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !drillDown ? <p className="text-sm text-gray-500">Line-by-line figures are not part of your access.</p> : null}
        </DetailsModal>
      )}
    </>
  );
}

// ---- Grants -----------------------------------------------------------------------------------

export function GrantsPanel({ rows, today, selectedOnly }: { rows: GovernanceGrant[]; today: string; selectedOnly: boolean }) {
  const [viewing, setViewing] = useState<number | null>(null);
  const columns: RegisterColumn<GovernanceGrant>[] = [
    { header: "Grant", cell: (row) => <span><span className="block font-medium text-gray-900">{row.name}</span><span className="text-xs text-gray-500">{row.project || "General"}</span></span>, exportValue: (row) => row.name },
    { header: "Donor", cell: (row) => row.donor, exportValue: (row) => row.donor },
    { header: "Received", cell: (row) => money(row.received), exportValue: (row) => num(row.received), align: "right" },
    { header: "Spent", cell: (row) => money(row.spent), exportValue: (row) => num(row.spent), align: "right" },
    { header: "Left", cell: (row) => <span className={num(row.balance) < 0 ? "text-red-700" : undefined}>{money(row.balance)}</span>, exportValue: (row) => num(row.balance), align: "right" },
    { header: "Spent of received", cell: (row) => <UsageBar value={row.utilization_percent} />, exportValue: (row) => `${row.utilization_percent}%` },
    { header: "Status", cell: (row) => <StatusDot {...grantStatus(row, today)} />, exportValue: (row) => grantStatus(row, today).label },
    { header: "Committed", cell: (row) => money(row.committed), exportValue: (row) => num(row.committed), exportOnly: true },
    { header: "Funding period", cell: () => "", exportValue: (row) => `${row.funding_period_start} to ${row.funding_period_end}`, exportOnly: true },
    { header: "Restriction", cell: () => "", exportValue: (row) => restrictionLabel(row.restriction), exportOnly: true },
  ];
  const row = viewing ? rows.find((item) => item.id === viewing) : undefined;
  return (
    <>
      <ClientRegister
        title="Grants and donor funds"
        description={selectedOnly ? "The grants the school has chosen to share with you: what was promised, received and spent." : "Approved grants: what each donor promised, what has been received and what has been spent."}
        rows={rows}
        config={grantConfig(today)}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["grant", "grants"]}
        exportName={{ file: "governance-grants", title: "Grants and Donor Funds" }}
        emptyText={selectedOnly ? "No grants have been shared with you yet." : "No approved grants yet."}
      />
      {row && (
        <DetailsModal
          title={row.name}
          headline={{ label: `${row.donor} · ${row.project || "General"}`, value: `${money(row.balance)} left`, note: `${money(row.spent)} spent of ${money(row.received)} received (${percent(row.utilization_percent)})` }}
          status={grantStatus(row, today)}
          facts={[
            ["Committed by the donor", money(row.committed)],
            ["Still to be received", money(num(row.committed) - num(row.received))],
            ["Funding period", `${shortDate(row.funding_period_start)} – ${shortDate(row.funding_period_end)}`],
            ["Restriction", restrictionLabel(row.restriction)],
            ["Approved by", row.approved_by || "Not recorded"],
            ["Approved on", stamp(row.approved_at) || "—"],
          ]}
          onClose={() => setViewing(null)}
        >
          <UsageBar value={row.utilization_percent} label="Spent of what has been received" />
        </DetailsModal>
      )}
    </>
  );
}

// ---- Approvals --------------------------------------------------------------------------------

export function ApprovalsPanel({ approvals, period }: { approvals: NonNullable<GovernanceDashboard["approvals"]>; period: string }) {
  const columns: RegisterColumn<GovernanceApproval>[] = [
    { header: "Approved", cell: (row) => <span className="whitespace-nowrap">{stamp(row.approved_at)}</span>, exportValue: (row) => stamp(row.approved_at) },
    { header: "Document", cell: (row) => <span className="font-medium text-gray-900">{documentLabel(row.document_type)}</span>, exportValue: (row) => documentLabel(row.document_type) },
    { header: "Record", cell: (row) => (row.object_id ? <span className="tabular-nums text-gray-500">#{row.object_id}</span> : "—"), exportValue: (row) => row.object_id ?? "" },
    { header: "Approved by", cell: (row) => row.approved_by, exportValue: (row) => row.approved_by },
  ];
  return (
    <div className="space-y-4">
      <div className="card p-5">
        <p className="text-sm text-gray-600"><span className="font-semibold text-gray-900">{approvals.total}</span> financial {approvals.total === 1 ? "document was" : "documents were"} approved {period}.</p>
        {approvals.by_document_type.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {approvals.by_document_type.map((row) => (
              <span key={row.content_type__model} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">{documentLabel(row.content_type__model)} <span className="font-semibold tabular-nums">{row.count}</span></span>
            ))}
          </div>
        )}
        {approvals.total > approvals.recent.length && <p className="mt-2 text-xs text-gray-500">The latest {approvals.recent.length} are listed below.</p>}
      </div>
      <ClientRegister
        title="Approvals"
        description="Who approved each financial document, and when. Nothing can be approved or changed from here."
        rows={approvals.recent}
        config={approvalConfig}
        columns={columns}
        rowKey={(row) => `${row.document_type}-${row.object_id ?? ""}-${row.approved_at}`}
        noun={["approval", "approvals"]}
        exportName={{ file: "governance-approvals", title: "Financial Approvals" }}
        emptyText="Nothing was approved in this period."
      />
    </div>
  );
}

// ---- Accounts (drill-down) --------------------------------------------------------------------

export function AccountsPanel({ rows, asOf, period }: { rows: GovernanceAccountBalance[]; asOf: string; period: string }) {
  const columns: RegisterColumn<GovernanceAccountBalance>[] = [
    { header: "Code", cell: (row) => <span className="tabular-nums text-gray-500">{row.code}</span>, exportValue: (row) => row.code },
    { header: "Account", cell: (row) => <span className={row.has_children ? "font-semibold text-gray-900" : "text-gray-800"} style={{ paddingLeft: `${row.depth * 1.25}rem` }}>{row.name}{row.is_contra_account && <span className="ml-1.5 text-xs font-normal text-gray-500">(reduces its group)</span>}</span>, exportValue: (row) => `${"  ".repeat(row.depth)}${row.name}` },
    { header: "Type", cell: (row) => ACCOUNT_TYPES[row.account_type] ?? row.account_type, exportValue: (row) => ACCOUNT_TYPES[row.account_type] ?? row.account_type },
    { header: "Balance", cell: (row) => <span className={num(row.balance) < 0 ? "text-red-700" : undefined}>{money(row.balance)}</span>, exportValue: (row) => num(row.balance), align: "right" },
  ];
  return (
    <ClientRegister
      title="Accounts"
      description={`Assets, liabilities and fund accounts as at ${shortDate(asOf)}; income and expense accounts for ${period}. A main account includes the accounts beneath it.`}
      rows={rows}
      config={accountConfig}
      columns={columns}
      rowKey={(row) => row.id}
      noun={["account", "accounts"]}
      exportName={{ file: "governance-accounts", title: "Account Balances" }}
      emptyText="The chart of accounts is empty."
    />
  );
}

// ---- Pay (salary authorization) ---------------------------------------------------------------

export function PayPanel({ summary }: { summary: NonNullable<GovernanceDashboard["salary_summary"]> }) {
  const columns: RegisterColumn<GovernanceStaffPay>[] = [
    { header: "Staff member", cell: (row) => <span className="font-medium text-gray-900">{row.staff}</span>, exportValue: (row) => row.staff },
    { header: "Position", cell: (row) => row.position, exportValue: (row) => row.position },
    { header: "Basic pay", cell: (row) => `${row.currency} ${num(row.salary).toLocaleString("en-GB")}`, exportValue: (row) => num(row.salary), align: "right" },
    { header: "Allowances", cell: (row) => (num(row.allowances) ? `${row.currency} ${num(row.allowances).toLocaleString("en-GB")}` : "—"), exportValue: (row) => num(row.allowances), align: "right" },
    { header: "Monthly pay", cell: (row) => <span className="font-semibold">{row.currency} {num(row.gross).toLocaleString("en-GB")}</span>, exportValue: (row) => num(row.gross), align: "right" },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Confidential. You see pay because your access includes salary authorization. Do not copy it into papers that go beyond the people entitled to it.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Salaried staff", String(summary.salaried_staff)],
          ["Basic pay a month", money(summary.monthly_salary_total)],
          ["Allowances a month", money(summary.monthly_allowance_total)],
          ["Total a month", money(summary.monthly_gross_total)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-4"><p className="text-xs font-medium text-gray-500">{label}</p><p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{value}</p></div>
        ))}
      </div>
      {summary.staff ? (
        <ClientRegister
          title="Pay by person"
          description="Active staff and what each is paid this month, allowances included."
          rows={summary.staff}
          config={payConfig}
          columns={columns}
          rowKey={(row) => `${row.staff}-${row.position}`}
          noun={["person", "people"]}
          exportName={{ file: "governance-pay", title: "Staff Pay (Confidential)" }}
          emptyText="No active staff have a salary recorded."
        />
      ) : (
        <p className="text-sm text-gray-500">Pay by person is not part of your access; only the totals are.</p>
      )}
    </div>
  );
}
